<template>
  <v-autocomplete
    :clearable="true"
    :class="{ 'mb-4': persistentHint }"
    :data-cy="`user-picker-${attributeName}`"
    :dense="dense"
    :filled="filled"
    :hide-details="hideDetails"
    :hint="hint"
    :items="sortedUserList"
    :item-text="formatUserForDisplay"
    :label="pickerLabel"
    :chips="chips"
    :deletable-chips="chips"
    :multiple="multiple"
    :outlined="outlined"
    :small-chips="chips"
    :persistent-hint="persistentHint"
    :rules="rules"
    :single-line="singleLine"
    :value="multiple ? (value || []) : value"
    @input="$emit('input', $event)"
    item-value="username"
    aria-live="polite"
  >
    <template v-slot:item="{ item }">
      <div v-if="item.username" :class="{ 'font-italic': item.active === 0 || item.active === false }">
        {{ formatUserForDisplay(item) }}
      </div>
      <div v-else>(Unassigned)</div>
    </template>
  </v-autocomplete>
</template>

<script>
import { cloneDeep } from "lodash";
import { mapActions, mapState } from "vuex";

export default {
  computed: {
    ...mapState({
      userList: state => state.user.userList,
    }),
    hint() {
      // Only show hint if the currently assigned user is inactive or has excluded role
      if (!this.value) {
        return "";
      }
      const currentUser = this.userList.find(user => user.username === this.value);
      const isCurrentUserInactive = currentUser && (currentUser.active === 0 || currentUser.active === false);

      // Check if current user has an excluded role
      const isCurrentUserExcluded = currentUser && this.excludeRoles && this.excludeRoles.includes(currentUser.role);

      if (isCurrentUserExcluded) {
        return "This user does not have the required permissions and should be reassigned.";
      }
      return isCurrentUserInactive ? "This user is inactive." : "";
    },
    persistentHint() {
      return this.hint !== "";
    },
    pickerLabel() {
      const labelText = this.isRequired ? `${this.label} *` : this.label;
      return labelText;
    },
    rules() {
      return this.setRules();
    },
    filteredUserList() {
      let users = this.userList;

      // Filter by role if roleFilter prop is provided
      if (this.roleFilter) {
        users = users.filter(user => user.role === this.roleFilter);
      }

      // Exclude users with specific roles if excludeRoles prop is provided
      if (this.excludeRoles) {
        users = users.filter(user => !this.excludeRoles.includes(user.role));
      }

      if (!this.unAssignedIncluded) {
        return users;
      }

      const unAssignedUserList = cloneDeep(users);
      unAssignedUserList.unshift({
        active: true,
        role: null,
        username: null,
      });

      return unAssignedUserList;
    },
    filteredUserListWithActive() {
      const users = this.filteredUserList; // Already has role filtering applied

      // Get array of selected usernames (handle both single and multi-select)
      const selectedUsernames = this.getUsernamesArray(this.value);

      // Include initial value(s) to allow reselection during edit session
      const initialUsernames = this.getUsernamesArray(this.initialValue);

      // Combine current selection and initial selection
      const allowedUsernames = [...selectedUsernames, ...initialUsernames];

      // Filter out inactive users, but keep any that are currently or initially selected
      return users.filter(user => {
        // Include if active (this handles unassigned option too, since it has active: true)
        if (user.active === 1 || user.active === true) return true;

        // Include if currently selected OR was initially selected (even if inactive)
        return allowedUsernames.includes(user.username);
      });
    },
    sortedUserList() {
      // Sort users: active users first, then inactive users (both alphabetically by last name)
      const users = [...this.filteredUserListWithActive];
      return users.sort((a, b) => {
        // If one is unassigned (null username), keep it at top
        if (!a.username) return -1;
        if (!b.username) return 1;

        // Check active status
        const aActive = a.active === 1 || a.active === true;
        const bActive = b.active === 1 || b.active === true;

        // Active users before inactive users
        if (aActive && !bActive) return -1;
        if (!aActive && bActive) return 1;

        // Within same status, sort alphabetically by last name, then first name
        const aName = `${a.lastName || ""} ${a.firstName || ""}`.trim() || a.username;
        const bName = `${b.lastName || ""} ${b.firstName || ""}`.trim() || b.username;
        return aName.localeCompare(bName);
      });
    },
  },
  async created() {
    if (this.userList === undefined || this.userList === null || this.userList.length < 1) {
      try {
        await this.loadUserList();
      } catch (e) {
        console.error(e);
        this.$notifyError({ message: "Unable to fetch user list" });
      }
    }

    this.loading = false;
  },
  data() {
    return {
      loading: true,
      initialValue: null,
      hasSetInitialValue: false,
    };
  },
  watch: {
    value: {
      handler(newValue) {
        // Capture the first non-null/non-undefined value as initialValue
        if (!this.hasSetInitialValue && newValue !== null && newValue !== undefined) {
          // Skip empty arrays for multi-select
          if (this.multiple && Array.isArray(newValue) && newValue.length === 0) {
            return;
          }
          // Skip empty strings for single-select
          if (!this.multiple && newValue === "") {
            return;
          }

          this.initialValue = newValue;
          this.hasSetInitialValue = true;
        }
      },
      immediate: true,
    },
  },
  methods: {
    getUsernamesArray(value) {
      // Convert value to array of usernames for both single and multi-select modes
      if (!this.multiple) {
        return value ? [value] : [];
      }
      return Array.isArray(value) ? value : [];
    },
    formatUserForDisplay(user) {
      const { firstName, lastName, username, email, active } = user;
      if (!username) {
        return "(Unassigned)";
      }

      const isActive = active === 1 || active === true;

      if (!firstName || !lastName) {
        return isActive ? username : `${username} - Inactive`;
      }

      const displayName = `${firstName} ${lastName} (${email})`;
      return isActive ? displayName : `${displayName} - Inactive`;
    },
    setRules() {
      if (this.isRequired) {
        return [v => !!v || `${this.fieldName} is required`];
      }
      return [() => true];
    },
    ...mapActions({
      loadUserList: "user/load",
    }),
  },
  props: {
    attributeName: {
      type: String,
      default: "",
    },
    dense: {
      type: Boolean,
      default: false,
    },
    fieldName: {
      type: String,
      default: "",
    },
    filled: {
      type: Boolean,
      default: false,
    },
    hideDetails: {
      type: Boolean,
      default: false,
    },
    isRequired: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: "Select user",
    },
    chips: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    roleFilter: {
      type: String,
      default: null,
    },
    excludeRoles: {
      type: Array,
      default: null,
    },
    singleLine: {
      type: Boolean,
      default: false,
    },
    trimUserName: {
      type: Boolean,
      default: false,
    },
    unAssignedIncluded: {
      type: Boolean,
      default: false,
    },
    value: {},
  },
};
</script>
